import { createForm } from "../../components/form/createForm.ts";
import { InferOutput, object, pipe } from "valibot";
import PageHeader from "../PageHeader.tsx";
import { useTranslation } from "../../state/i18n.tsx";
import moreappWhiteIcon from "../../assets/moreapp-icon-white.svg";
import Section from "../Section.tsx";
import { Form } from "../../components/form/Form.tsx";
import { TextInput } from "../../components/inputs/TextInput.tsx";
import { SingleSelectWithSearchInput } from "../../components/menu/SingleSelectWithSearch.tsx";
import CountriesService from "../../services/CountriesService.ts";
import { TextButton } from "../../components/button/Button.tsx";
import { SingleSelectInput } from "../../components/menu/Select.tsx";
import { createSignal, Show } from "solid-js";
import { email, requireText } from "../../utils/validation/base.ts";
import { customerSchema } from "../../types/Customer.ts";
import { MAX_CHARACTERS_MD, MAX_CHARACTERS_SM, MINIMUM_ACTION_LOADING_DELAY } from "../../constants.ts";
import Layout from "../Layout.tsx";
import pMinDelay from "p-min-delay";
import ApiService, { CustomerInfoDto } from "../../services/ApiService.ts";
import InlineMessage from "../../components/inlineMessage/InlineMessage.tsx";
import { directUserToSupport } from "../../utils/supportUtil.ts";
import { getCompanySectors } from "./CompanyForm.tsx";
import { Portal } from "solid-js/web";
import { Dialog } from "@ark-ui/solid";

export const CreateCustomerSchema = object({
  companyName: customerSchema.entries.name,
  billingEmail: pipe(requireText(MAX_CHARACTERS_MD), email(MAX_CHARACTERS_MD)),
  country: requireText(MAX_CHARACTERS_SM),
  sector: requireText(MAX_CHARACTERS_SM),
});

type CreateCustomerFormProps = {
  close: () => void;
};

const CreateCustomerForm = (props: CreateCustomerFormProps) => {
  const { t, currentLanguage } = useTranslation();
  const [showWarning, setShowWarning] = createSignal(false);

  const createCustomer = ApiService.createCustomer();

  const form = createForm<InferOutput<typeof CreateCustomerSchema>>(
    CreateCustomerSchema,
    async ({ companyName, billingEmail, country, sector }) =>
      await pMinDelay(
        createCustomer.mutateAsync({
          name: companyName,
          emailAddress: billingEmail,
          language: currentLanguage(),
          segment: sector,
          country,
        }),
        MINIMUM_ACTION_LOADING_DELAY,
      )
        .then((customer: CustomerInfoDto) => window.open(`/customers/${customer.customerId}`, "_self"))
        .catch(() => setShowWarning(true)),
    {
      companyName: undefined,
      billingEmail: undefined,
      country: undefined,
      sector: undefined,
    },
  );
  const countries = CountriesService.getCountries();

  return (
    <Dialog.Root
      open
      onOpenChange={(details) => {
        if (!details.open) {
          props.close();
        }
      }}
      aria-label={t(($) => $.USER_MENU_CREATE_ACCOUNT_TITLE)}
    >
      <Portal>
        <Dialog.Positioner class="fixed inset-0 z-50 overflow-y-auto bg-surface-default">
          <Dialog.Content>
            <Layout>
              <PageHeader
                title={t(($) => $.USER_MENU_CREATE_ACCOUNT_TITLE)}
                subTitle={t(($) => $.USER_MENU_CREATE_ACCOUNT_SUBTITLE)}
                logoSrc={moreappWhiteIcon}
              />
              <Section fullHeight>
                <div class="flex flex-col gap-10">
                  <div>
                    <h2 class="mb-6 text-header-xl text-primary">{t(($) => $.USER_MENU_CREATE_ACCOUNT_FORM_TITLE)}</h2>
                    <Form form={form} class="flex size-full grow flex-col">
                      {({ value, error, updateValue }) => (
                        <div class="flex h-full grow flex-col">
                          <div class="flex w-full flex-col gap-y-6">
                            <TextInput
                              label={t(($) => $.USER_MENU_CREATE_ACCOUNT_FORM_COMPANY_NAME)}
                              required
                              value={value("companyName")}
                              errorMessage={error("companyName")}
                              onInput={(e) => updateValue("companyName", e.target.value)}
                              onBlur={(e) => updateValue("companyName", e.target.value)}
                            />
                            <TextInput
                              label={t(($) => $.USER_MENU_CREATE_ACCOUNT_FORM_BILLING_EMAIL)}
                              required
                              value={value("billingEmail")}
                              errorMessage={error("billingEmail")}
                              onInput={(e) => updateValue("billingEmail", e.target.value)}
                              onBlur={(e) => updateValue("billingEmail", e.target.value)}
                            />
                            <SingleSelectWithSearchInput<string>
                              label={t(($) => $.USER_MENU_CREATE_ACCOUNT_FORM_COUNTRY)}
                              options={{
                                [t(($) => $.USER_MENU_CREATE_ACCOUNT_FORM_COUNTRY)]:
                                  countries.data?.countries.map((country) => ({
                                    variant: "text",
                                    value: country.alpha2Code,
                                    label: country.name,
                                  })) ?? [],
                              }}
                              required
                              value={value("country")}
                              errorMessage={error("country")}
                              onSelect={(value) => updateValue("country", value ?? "")}
                              width="matchTrigger"
                            />
                            <SingleSelectInput<string>
                              label={t(($) => $.USER_MENU_CREATE_ACCOUNT_FORM_SECTOR)}
                              options={getCompanySectors(t).map((sector) => ({
                                variant: "text",
                                value: sector.value,
                                label: sector.label,
                              }))}
                              required
                              value={value("sector")}
                              errorMessage={error("sector")}
                              onSelect={(value) => updateValue("sector", value)}
                              sameWidth
                            />
                          </div>
                        </div>
                      )}
                    </Form>
                    <Show when={showWarning()}>
                      <InlineMessage
                        variant="warning"
                        label={t(($) => $.ONBOARDING_PAGE_SUBMIT_ERROR_LABEL)}
                        action={{
                          label: t(($) => $.ONBOARDING_PAGE_SUBMIT_ERROR_ACTION_LABEL),
                          onClick: directUserToSupport,
                        }}
                        class="mt-6"
                      />
                    </Show>
                  </div>
                  <div class="flex justify-end gap-2">
                    <TextButton
                      text={t(($) => $.USER_MENU_CREATE_ACCOUNT_CANCEL)}
                      variant="secondary"
                      onClick={() => {
                        form.reset();
                        setShowWarning(false);
                        props.close();
                      }}
                    />
                    <TextButton
                      text={t(($) => $.USER_MENU_CREATE_ACCOUNT_CREATE)}
                      type="submit"
                      form={form.id}
                      loading={form.isSubmitting()}
                      disabled={form.submitDisabled()}
                    />
                  </div>
                </div>
              </Section>
            </Layout>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CreateCustomerForm;
